import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Loader2, Search, Shield, ShieldOff, Ban, Trash2, Pencil } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { toast } from '../../hooks/use-toast';
import UserProfileDialog from '../../components/UserProfileDialog';
import AdminEditUserDialog from '../../components/AdminEditUserDialog';
import { UserAvatar } from '../../components/VapeAvatars';

const UsersSection = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [editUserId, setEditUserId] = useState(null);
  const limit = 25;

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    params.set('page', String(page));
    params.set('limit', String(limit));
    api.get(`/admin/users?${params.toString()}`).then((r) => {
      setUsers(r.data.items);
      setTotal(r.data.total);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page]);

  const toggleAdmin = async (u) => {
    if (u.user_id === me.user_id) { toast({ title: 'No puedes cambiar tu propio rol' }); return; }
    try {
      await api.patch(`/admin/users/${u.user_id}`, { is_admin: !u.is_admin });
      toast({ title: !u.is_admin ? 'Promovido a admin' : 'Rol removido' });
      load();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail }); }
  };

  const toggleBlocked = async (u) => {
    try {
      await api.patch(`/admin/users/${u.user_id}`, { blocked: !u.blocked });
      toast({ title: !u.blocked ? 'Usuario bloqueado' : 'Usuario desbloqueado' });
      load();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail }); }
  };

  const remove = async (u) => {
    if (u.user_id === me.user_id) { toast({ title: 'No puedes eliminarte a ti mismo' }); return; }
    if (!window.confirm(`¿Eliminar al usuario "${u.name || u.email || u.phone}"? Esta acción es permanente.`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast({ title: 'Usuario eliminado' });
      load();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail }); }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div>
      <h1 className="text-2xl md:text-3xl font-black text-white">Usuarios</h1>
      <p className="text-white/50 text-sm mt-1">{total} usuarios registrados</p>

      <div className="mt-4 relative">
        <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); load(); } }}
          placeholder="Buscar por nombre, email o teléfono..."
          className="pl-9 bg-neutral-950 border-white/10 text-white"
        />
      </div>

      {loading ? <Loader2 className="w-6 h-6 animate-spin text-orange-500 mt-6" /> : (
        <div className="mt-4 bg-neutral-950 border border-white/10 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-white/5 text-white/60">
              <tr>
                <th className="text-left px-3 py-2">Usuario</th>
                <th className="text-left px-3 py-2">Contacto</th>
                <th className="text-right px-3 py-2">Pedidos</th>
                <th className="text-left px-3 py-2">Registro</th>
                <th className="text-left px-3 py-2">IP</th>
                <th className="text-center px-3 py-2">Rol</th>
                <th className="text-right px-3 py-2 w-40">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-t border-white/5 hover:bg-white/5 cursor-pointer" onClick={(e) => { if (e.target.tagName !== 'BUTTON' && e.target.tagName !== 'SVG' && e.target.tagName !== 'path') setProfileUser(u.user_id); }}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <UserAvatar picture={u.picture} name={u.name} phone={u.phone} email={u.email} size={36} />
                      <div className="min-w-0">
                        <p className="text-white font-semibold truncate hover:text-orange-400">{u.name || 'Sin nombre'} {u.last_name || ''}</p>
                        <p className="text-white/50 text-xs">{u.auth_provider || 'phone'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <p className="text-white/80 text-xs">{u.email || <span className="text-white/40">sin correo</span>}</p>
                    <p className="text-white/60 text-xs">{u.phone || ''}</p>
                  </td>
                  <td className="px-3 py-2 text-right text-white font-bold">{u.orders_count || 0}</td>
                  <td className="px-3 py-2 text-white/60 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString('es-MX') : '-'}</td>
                  <td className="px-3 py-2 text-white/40 text-xs font-mono">{u.registration_ip || '-'}</td>
                  <td className="px-3 py-2 text-center">
                    {u.is_admin ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-400 border border-orange-500/30">Admin</span> : <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/5 text-white/60 border border-white/10">Cliente</span>}
                    {u.blocked && <div className="mt-1 text-[10px] font-bold text-rose-400">Bloqueado</div>}
                  </td>
                  <td className="px-3 py-2 text-right space-x-1">
                    {(u.registration_ip || u.last_ip) && (
                      <a
                        href={`https://who.is/whois-ip/ip-address/${u.registration_ip || u.last_ip}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 rounded-md bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 inline-flex items-center justify-center"
                        title={`WhoIs ${u.registration_ip || u.last_ip}`}
                        data-testid={`admin-user-whois-${u.user_id}`}
                      >
                        <Search className="w-4 h-4" />
                      </a>
                    )}
                    <button onClick={() => setEditUserId(u.user_id)} className="w-8 h-8 rounded-md bg-orange-500/15 hover:bg-orange-500/25 text-orange-300 inline-flex items-center justify-center" title="Editar datos del usuario" data-testid={`admin-user-edit-${u.user_id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => toggleAdmin(u)} className={`w-8 h-8 rounded-md inline-flex items-center justify-center ${u.is_admin ? 'bg-orange-500/15 text-orange-400' : 'bg-white/5 text-white/70 hover:bg-white/10'}`} title={u.is_admin ? 'Quitar admin' : 'Hacer admin'}>
                      {u.is_admin ? <ShieldOff className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                    </button>
                    <button onClick={() => toggleBlocked(u)} className={`w-8 h-8 rounded-md inline-flex items-center justify-center ${u.blocked ? 'bg-rose-500/20 text-rose-300' : 'bg-white/5 text-white/70 hover:bg-white/10'}`} title={u.blocked ? 'Desbloquear' : 'Bloquear'}>
                      <Ban className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(u)} className="w-8 h-8 rounded-md bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 inline-flex items-center justify-center" title="Eliminar">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-white/50">Sin usuarios.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <Button disabled={page === 1} onClick={() => setPage(page - 1)} variant="outline" className="bg-transparent border-white/20 text-white hover:bg-white/10">Anterior</Button>
          <span className="text-white/70 text-sm">Página {page} de {totalPages}</span>
          <Button disabled={page === totalPages} onClick={() => setPage(page + 1)} className="bg-orange-500 hover:bg-orange-600 text-black">Siguiente</Button>
        </div>
      )}

      <UserProfileDialog userId={profileUser} open={!!profileUser} onOpenChange={(v) => !v && setProfileUser(null)} />
      <AdminEditUserDialog userId={editUserId} open={!!editUserId} onOpenChange={(v) => !v && setEditUserId(null)} onSaved={load} />
    </div>
  );
};

export default UsersSection;
